import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaUser, FaCar, FaIdCard, FaPhoneAlt, FaEnvelope, FaCalendarAlt, FaStar, FaRoute, FaWallet, FaRupeeSign, FaFileUpload, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import DriverLayout from '../../layouts/DriverLayout';
import { HiShieldCheck } from 'react-icons/hi';
import { API_URL } from '../../config/apiConfig';
import { getDriverProfile, getBookingHistory, reset } from '../../features/driver/driverSlice';
import { getDriverWallet } from '../../features/wallet/walletSlice';

const DriverProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { driver, bookingHistory, loading, error } = useSelector((state) => state.driver);
  const { wallet } = useSelector((state) => state.wallet);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyRequested, setHistoryRequested] = useState(false);

  useEffect(() => {
    // First check if we already have driver profile data
    if (driver && driver._id) {
      setLoadingProfile(false);
    } else {
      if (driverAuth) {
        const driverId = (driverAuth._id) || (driverAuth.driver && driverAuth.driver._id) || (driverAuth.id) || (driverAuth.driver && driverAuth.driver.id);
        if (driverId) {
          dispatch(getDriverProfile(driverId));
        } else {
          toast.error('Unable to load driver profile. Please try logging in again.');
          setLoadingProfile(false);
        }
      } else {
        setLoadingProfile(false);
      }
    }

    return () => {
      dispatch(reset());
    };
  }, [dispatch, driverAuth, driver]);

  // Load booking history and wallet when driver profile is loaded
  useEffect(() => {
    if (driver && driver._id && !historyRequested) {
      dispatch(getBookingHistory(driver._id));
      dispatch(getDriverWallet(driver._id));
      setHistoryRequested(true);
    }

    if (bookingHistory) {
      setLoadingHistory(false);
    }
  }, [dispatch, driver, historyRequested, bookingHistory]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(reset());
      setLoadingProfile(false);
    }
    
    // Set loading to false when we get the driver profile
    if (driver && driver._id) {
      setLoadingProfile(false);
    }
  }, [error, dispatch, driver]);

  // Ensure all hooks are declared before any early returns to keep hook order stable
  const [showEditModal, setShowEditModal] = useState(false);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleModel: '',
    vehicleNumber: '',
    licenseNumber: '',
    licenseExpiry: ''
  });
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // Document upload state
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentFiles, setDocumentFiles] = useState({});
  const [uploadingDocuments, setUploadingDocuments] = useState(false);

  const documentTypes = [
    { key: 'aadhaarCard', label: 'Aadhaar Card' },
    { key: 'driversLicense', label: 'Driver\'s License' },
    { key: 'vehicleRC', label: 'Vehicle RC' },
    { key: 'insuranceCertificate', label: 'Insurance Certificate' },
    { key: 'driverPhoto', label: 'Driver Photo' },
    { key: 'pucCertificate', label: 'PUC Certificate' },
    { key: 'fitnessCertificate', label: 'Fitness Certificate' },
    { key: 'routePermit', label: 'Route Permit' },
    { key: 'vehiclePhotoFront', label: 'Vehicle Photo (Front)' },
    { key: 'vehiclePhotoBack', label: 'Vehicle Photo (Back)' }
  ];

  useEffect(() => {
    if (driver) {
      setFormState({
        name: driver.name || '',
        email: driver.email || '',
        phone: driver.phone || '',
        vehicleModel: driver.vehicleModel || '',
        vehicleNumber: driver.vehicleNumber || '',
        licenseNumber: driver.licenseNumber || '',
        licenseExpiry: driver.licenseExpiry ? new Date(driver.licenseExpiry).toISOString().split('T')[0] : ''
      });
      setAvatarPreview(driver.profilePhoto || '');
    }
  }, [driver]);

  if (loadingProfile || loading) {
    return (
      <DriverLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
          <div className="ml-4 text-gray-700 font-medium">Loading driver profile...</div>
        </div>
      </DriverLayout>
    );
  }

  if (!driver) {
    return (
      <DriverLayout>
        <div className="flex flex-col items-center justify-center h-full">
          <h2 className="text-2xl font-bold mb-4 text-red-600">Profile Not Found</h2>
          <p className="text-gray-300">Please log in to view your profile</p>
        </div>
      </DriverLayout>
    );
  }

  // Get driver name - handle different possible structures
  const getDriverName = () => {
    if (driver && driver.name) return driver.name;
    if (driverAuth && driverAuth.name) return driverAuth.name;
    if (driverAuth && driverAuth.driver && driverAuth.driver.name) return driverAuth.driver.name;
    return 'Driver';
  };

  // Calculate joining date
  const joiningDate = driver && driver.createdAt ? new Date(driver.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : 'Not available';

  // Calculate experience (months since joining)
  const calculateExperience = () => {
    if (!driver || !driver.createdAt) return 0;
    const joinDate = new Date(driver.createdAt);
    const currentDate = new Date();
    const diffInMonths = (currentDate.getFullYear() - joinDate.getFullYear()) * 12 + 
                         (currentDate.getMonth() - joinDate.getMonth());
    return diffInMonths;
  };
  
  const experience = calculateExperience();

  // Get driver availability status
  const isAvailable = driver && driver.isAvailable ? true : false;

  // Get driver rating
  const driverRating = driver && driver.ratings && driver.ratings > 0 ? driver.ratings.toFixed(1) : 'No ratings yet';

  // Render currency with separate symbol and amount for modern look
  const renderCurrency = (amount) => {
    try {
      const parts = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).formatToParts(amount || 0);
      const currency = parts.find(p => p.type === 'currency')?.value || '₹';
      const integer = parts.filter(p => p.type === 'integer' || p.type === 'group').map(p => p.value).join('');
      const fraction = parts.find(p => p.type === 'fraction')?.value ? '.' + parts.find(p => p.type === 'fraction')?.value : '';
      return (
        <div className="flex items-baseline">
          <span className="text-lg text-indigo-600 mr-2">{currency}</span>
          <span className="text-3xl font-extrabold text-gray-900">{integer}{fraction}</span>
        </div>
      );
    } catch (e) {
      return <span className="text-3xl font-extrabold text-gray-900">{formatCurrency(amount)}</span>;
    }
  };

  // Calculate total earnings from bookings (ONLINE ONLY)
  const calculateTotalEarnings = () => {
    if (!bookingHistory || bookingHistory.length === 0) return 0;

    return bookingHistory.reduce((total, booking) => {
      if (booking.status === 'completed') {
        const payment = (booking.paymentMethod || booking.paymentMode || '').toString().toLowerCase();
        const isOnline = payment && payment !== 'cod' && payment !== 'cash';
        if (isOnline) {
          const amount = booking.fare || booking.totalAmount || booking.amount || 0;
          return total + Number(amount);
        }
      }
      return total;
    }, 0);
  };

  const totalEarningsFromBookings = calculateTotalEarnings();

  // Currency formatter
  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

  // Prefer wallet-provided totals for accuracy
  const computedTotalEarnings = (() => {
    let total = wallet?.totalEarnings || wallet?.totalOnlineEarnings || 0;
    if ((!total || total === 0) && wallet?.transactions) {
      total = wallet.transactions
        .filter(t => t && (t.type === 'online_ride_earning' || t.type === 'ride_earning' || t.type === 'earning'))
        .reduce((sum, t) => sum + (t.amount || 0), 0);
    }
    return total || totalEarningsFromBookings;
  })();

  const walletBalance = wallet?.balance || 0;

  // Calculate completed trips count
  const completedTrips = bookingHistory ? bookingHistory.filter(booking => booking.status === 'completed').length : 0;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const validateForm = () => {
    if (!formState.name || !formState.phone) {
      toast.error('Name and phone are required');
      return false;
    }
    if (!/^\d{10}$/.test(formState.phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const token = (driverAuth && driverAuth.token) || localStorage.getItem('driverToken') || localStorage.getItem('token');
      const payload = { ...formState };
      if (avatarFile) {
        // convert to base64
        const toBase64 = (file) => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = (err) => reject(err);
          reader.readAsDataURL(file);
        });
        payload.profilePhotoBase64 = await toBase64(avatarFile);
      }

      const res = await axios.put(`${API_URL}/driver/auth/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data && res.data.success) {
        toast.success('Profile updated successfully');
        setShowEditModal(false);
        // refresh driver profile
        const driverId = driver._id;
        if (driverId) {
          // refresh driver profile
          dispatch(getDriverProfile(driverId));
        }
      } else {
        toast.error(res.data?.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Profile save error:', err);
      console.error('Profile save response data:', err.response?.data);
      console.error('Profile save request config:', err.config);
      toast.error(err.response?.data?.message || err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentFileChange = (e, docKey) => {
    const file = e.target.files[0];
    if (file) {
      setDocumentFiles(prev => ({ ...prev, [docKey]: file }));
    }
  };

  const handleUploadDocuments = async () => {
    if (Object.keys(documentFiles).length === 0) {
      toast.error('Please select at least one document to upload');
      return;
    }

    setUploadingDocuments(true);
    try {
      const token = (driverAuth && driverAuth.token) || localStorage.getItem('driverToken') || localStorage.getItem('token');
      const driverId = driver._id;

      const formData = new FormData();
      Object.entries(documentFiles).forEach(([docKey, file]) => {
        formData.append(docKey, file);
      });

      const res = await axios.put(
        `${API_URL}/driver/${driverId}/upload-documents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 150000
        }
      );

      if (res.data && res.data.status) {
        toast.success('Documents uploaded successfully');
        setShowDocumentModal(false);
        setDocumentFiles({});
        // Refresh driver profile to show updated documents
        dispatch(getDriverProfile(driverId));
      } else {
        toast.error(res.data?.message || 'Failed to upload documents');
      }
    } catch (err) {
      console.error('Document upload error:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to upload documents');
    } finally {
      setUploadingDocuments(false);
    }
  };

  const getDocumentStatus = (docKey) => {
    if (!driver.documents || !driver.documents[docKey]) {
      return { status: 'pending', url: '' };
    }
    const doc = driver.documents[docKey];
    return {
      status: doc.status || 'verified',
      url: doc.url || (typeof doc === 'string' ? doc : '')
    };
  };

  return (
    <DriverLayout>
      <div className="space-y-8">
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
          {/* Cover Header */}
          <div className="h-32 sm:h-44 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 relative">
            <div className="absolute inset-0 bg-grid-pattern opacity-10" />
          </div>

          {/* Profile Header Details */}
          <div className="px-6 pb-6 relative">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 -mt-16 sm:-mt-20 mb-6">
              <div className="flex flex-col sm:flex-row items-end gap-4 w-full md:w-auto">
                <div className="bg-white p-1.5 rounded-3xl h-28 w-28 sm:h-36 sm:w-36 flex items-center justify-center shadow-lg border border-slate-100 overflow-hidden z-10">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="avatar" className="h-full w-full object-cover rounded-2xl" />
                  ) : (
                    <div className="h-full w-full bg-slate-100 rounded-2xl flex items-center justify-center text-3xl font-black text-slate-700">
                      {getDriverName().charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                <div className="flex-grow pb-2">
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">{getDriverName()}</h1>
                    {driver.documentsVerified === true || driver.documentsVerified === "true" ? (
                      <span className="text-[10px] bg-emerald-50 text-emerald-600 font-extrabold uppercase px-2 py-0.5 rounded-md border border-emerald-100">
                        Verified
                      </span>
                    ) : null}
                  </div>
                  
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`h-2 w-2 rounded-full ${isAvailable ? 'bg-green-500' : 'bg-slate-350'}`} />
                    <span className={`text-xs font-bold ${isAvailable ? 'text-green-600' : 'text-slate-500'}`}>
                      {isAvailable ? 'Available for Rides' : 'Offline'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2.5 w-full sm:w-auto self-stretch sm:self-auto justify-end">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-5 py-2.5 bg-slate-50 border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold text-xs rounded-xl transition duration-200 whitespace-nowrap"
                >
                  Edit Profile
                </button>
                <button
                  onClick={() => {
                    if (driverAuth) navigate('/driver/change-password');
                    else navigate('/driver/forgot-password/send-otp');
                  }}
                  className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-5 py-2.5 bg-slate-900 hover:bg-slate-850 text-white font-semibold text-xs rounded-xl transition duration-200 shadow-sm whitespace-nowrap"
                >
                  Change Password
                </button>
              </div>
            </div>

            {/* Quick Metrics Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-5 rounded-2xl bg-slate-50/50 border border-slate-100">
              <div className="flex flex-col">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Rating</span>
                <span className="text-sm font-black text-slate-800 mt-1 flex items-center gap-1">
                  ⭐ {driverRating}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Completed Trips</span>
                <span className="text-sm font-black text-slate-800 mt-1">{completedTrips}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Acceptance</span>
                <span className="text-sm font-black text-slate-800 mt-1">{driver.acceptanceRate || '95'}%</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Experience</span>
                <span className="text-sm font-black text-slate-800 mt-1">{experience} <span className="text-[10px] font-normal text-slate-500">months</span></span>
              </div>
            </div>
          </div>
        </div>

        {/* Balance & Wallet Widget */}
        <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white rounded-3xl p-6 shadow-xl relative overflow-hidden border border-slate-800">
          <div className="absolute right-0 top-0 translate-x-16 -translate-y-16 w-48 h-48 bg-indigo-500/10 rounded-full blur-2xl" />
          
          <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
            <div>
              <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest block">Wallet Balance</span>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-xl text-indigo-300">₹</span>
                <span className="text-3xl font-black tracking-tight">{walletBalance.toLocaleString('en-IN')}</span>
              </div>
              <p className="text-[10px] text-slate-400 font-semibold mt-1">Available for instant withdrawal</p>
            </div>
            
            <button
              onClick={() => navigate('/driver/wallet/withdraw')}
              className="px-5 py-3 bg-white text-slate-900 hover:bg-slate-100 font-bold text-xs rounded-xl shadow transition duration-200"
            >
              Request Withdrawal
            </button>
          </div>
        </div>

        {/* Detailed Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Info */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
            <h2 className="text-sm font-black text-slate-855 uppercase tracking-widest border-b border-slate-100 pb-3 mb-4 flex items-center gap-2">
              👤 Personal Information
            </h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 text-sm">
                  <FaUser />
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Full Name</span>
                  <span className="text-xs font-bold text-slate-750">{driver.name}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 text-sm">
                  <FaEnvelope />
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Email Address</span>
                  <span className="text-xs font-bold text-slate-750">{driver.email}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 text-sm">
                  <FaPhoneAlt />
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Mobile Number</span>
                  <span className="text-xs font-bold text-slate-750">{driver.phone}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 text-sm">
                  <FaCalendarAlt />
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Registration Date</span>
                  <span className="text-xs font-bold text-slate-750">{joiningDate}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
            <h2 className="text-sm font-black text-slate-855 uppercase tracking-widest border-b border-slate-100 pb-3 mb-4 flex items-center gap-2">
              🚗 Vehicle Details
            </h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 text-sm">
                  <FaCar />
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Vehicle Model</span>
                  <span className="text-xs font-bold text-slate-750">{driver.vehicleModel}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 text-sm">
                  <FaCar />
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Vehicle Class</span>
                  <span className="text-xs font-bold text-slate-750">
                    {typeof driver.vehicleType === 'object' ? driver.vehicleType.name : (driver.vehicleType || 'Sedan')}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 text-sm">
                  <FaCar />
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">License Plate</span>
                  <span className="text-xs font-bold text-slate-750">
                    {driver.vehicleNumber ? driver.vehicleNumber.split('-')[0] : 'N/A'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 text-sm">
                  <FaIdCard />
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Driver License</span>
                  <span className="text-xs font-bold text-slate-750">
                    {driver.licenseNumber ? driver.licenseNumber.split('-')[0] : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Verification Documents Checklist */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm col-span-1 md:col-span-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4 mb-6">
              <div>
                <h2 className="text-sm font-black text-slate-855 uppercase tracking-widest flex items-center gap-2">
                  📂 Verification Documents
                </h2>
                <p className="text-[11px] text-slate-400 mt-1 font-semibold">Upload clear copies of your license and permits</p>
              </div>
              
              <button
                onClick={() => setShowDocumentModal(true)}
                className="flex items-center justify-center gap-1.5 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-755 text-white font-bold text-xs rounded-xl shadow-sm transition"
              >
                <FaFileUpload />
                Upload New Files
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {documentTypes.map((doc) => {
                const docStatus = getDocumentStatus(doc.key);
                const isVerified = docStatus.status === 'verified';

                return (
                  <div key={doc.key} className="border border-slate-100 rounded-2xl p-4 flex justify-between items-center bg-slate-50/30">
                    <div>
                      <p className="text-xs font-bold text-slate-800">{doc.label}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        {isVerified ? (
                          <FaCheckCircle className="text-emerald-500 text-xs" />
                        ) : (
                          <FaTimesCircle className="text-slate-300 text-xs" />
                        )}
                        <span className={`text-[10px] font-black uppercase tracking-wider ${isVerified ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {isVerified ? 'Verified' : 'Pending Upload'}
                        </span>
                      </div>
                    </div>

                    {docStatus.url && (
                      <a
                        href={docStatus.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 font-semibold text-[10px] rounded-lg transition"
                      >
                        View File
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Profile</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">Close</button>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center space-x-4">
                <div className="h-20 w-20 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center text-2xl text-gray-300">
                  {avatarPreview ? <img src={avatarPreview} alt="avatar" className="h-full w-full object-cover" /> : getDriverName().charAt(0)}
                </div>
                <div>
                  <label className="block text-sm text-gray-300">Upload avatar</label>
                  <input type="file" accept="image/*" onChange={handleAvatarChange} className="mt-1" />
                </div>
              </div>

              <input name="name" value={formState.name} onChange={handleInputChange} className="border border-gray-200 rounded-md p-2" placeholder="Full name" />
              <input name="email" value={formState.email} onChange={handleInputChange} className="border border-gray-200 rounded-md p-2" placeholder="Email" />
              <input name="phone" value={formState.phone} onChange={handleInputChange} className="border border-gray-200 rounded-md p-2" placeholder="Phone" />
              <div className="flex justify-end space-x-2 mt-4">
                <button onClick={() => setShowEditModal(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded">{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-lg my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Upload Documents</h3>
              <button onClick={() => setShowDocumentModal(false)} className="text-gray-500 hover:text-gray-700">×</button>
            </div>

            <div className="max-h-96 overflow-y-auto mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documentTypes.map((doc) => (
                  <div key={doc.key} className="border border-gray-300 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      {doc.label}
                    </label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => handleDocumentFileChange(e, doc.key)}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    />
                    {documentFiles[doc.key] && (
                      <p className="text-xs text-green-600 mt-1">✓ {documentFiles[doc.key].name}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowDocumentModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300"
                disabled={uploadingDocuments}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadDocuments}
                disabled={uploadingDocuments || Object.keys(documentFiles).length === 0}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {uploadingDocuments ? 'Uploading...' : 'Upload Documents'}
              </button>
            </div>
          </div>
        </div>
      )}

    </DriverLayout>
  );
};

export default DriverProfile;
